import { createElement } from 'react';
import { DndComponentsCtxt, DndModules } from './DndComponentsContext';
import { DefaultReactDndConstructs, ReactDndCtxt, ReactDndConstructs } from './ReactDndContext';
import { DndProviderProps } from 'react-dnd';
import { BackendFactory } from 'dnd-core';

type DragAndDropProviderProps = { enableDragDrop?: boolean };

type ReactDndProviderAndBackend = {
  HTML5Backend: BackendFactory;
  DndProvider: React.FC<DndProviderProps<any, any>>;
};

type DndModuleTypes = [
  // rbd
  DndModules,
  // react-dnd
  Omit<ReactDndConstructs, 'getEmptyImage'> & Pick<ReactDndProviderAndBackend, 'DndProvider'>,
  // react-dnd backend
  Pick<ReactDndConstructs, 'getEmptyImage'> & Pick<ReactDndProviderAndBackend, 'HTML5Backend'>
];

type DndModulePromises = [
  Promise<DndModuleTypes[0]>,
  Promise<DndModuleTypes[1]>,
  Promise<DndModuleTypes[2]>
];

function getDndModulesLoader(loadModulesPromises: () => DndModulePromises) {
  let result: [DndModules, ReactDndConstructs, ReactDndProviderAndBackend] | null = null,
    error: any,
    loadPromise: Promise<DndModuleTypes>;
  return () => {
    if (!loadPromise) {
      loadPromise = Promise.all(loadModulesPromises());
    }
    loadPromise
      .then((modules) => {
        result = [
          {
            Draggable: modules[0].Draggable,
            Droppable: modules[0].Droppable,
            DragDropContext: modules[0].DragDropContext,
          },
          {
            useDrag: modules[1].useDrag,
            useDragLayer: modules[1].useDragLayer,
            useDrop: modules[1].useDrop,
            getEmptyImage: modules[2].getEmptyImage,
          },
          {
            DndProvider: modules[1].DndProvider,
            HTML5Backend: modules[2].HTML5Backend,
          },
        ];
      })
      .catch((_error) => (error = _error));
    if (result) return result;
    else if (error) throw error;
    else throw loadPromise;
  };
}

const loadDndModules = getDndModulesLoader(() => [
  import('.') as any,
  import('react-dnd') as any,
  import('react-dnd-html5-backend') as any,
]);

export function DragAndDropProvider(props: React.PropsWithChildren<DragAndDropProviderProps>) {
  let rbdModules: DndModules | null = null;
  let reactdndConstructs: ReactDndConstructs = DefaultReactDndConstructs;
  let HTML5Backend: BackendFactory;
  let DndProvider: React.FC<React.PropsWithChildren<DndProviderProps<any, any>>>;

  if (props.enableDragDrop) {
    const result = loadDndModules();
    [rbdModules, reactdndConstructs, { HTML5Backend, DndProvider }] = result;
  }

  return (
    <DndComponentsCtxt.Provider value={rbdModules}>
      <ReactDndCtxt.Provider value={reactdndConstructs}>
        {DndProvider! && HTML5Backend! ? (
          <DndProvider backend={HTML5Backend}>{props.children}</DndProvider>
        ) : (
          props.children
        )}
      </ReactDndCtxt.Provider>
    </DndComponentsCtxt.Provider>
  );
}
